// Tarif-Modell des Rent-Portals.
// Das Hauptbackend liefert pro Standort eine Liste "tariffs". Fehlt sie (alte API), wird aus "price" ein Tarif abgeleitet.

export const TARIFF_ORDER = ["hour", "day", "night", "nightsub", "workday", "full"];
export const SUBSCRIPTIONS = ["nightsub", "workday", "full"];

const DEFAULTS = {
  hour: {},
  day: { start: "00:00", end: "24:00" },
  night: { start: "18:00", end: "07:00" },
  nightsub: { start: "18:00", end: "07:00" },
  workday: { start: "07:00", end: "19:00", weekdays: [1, 2, 3, 4, 5] },
  full: { start: "00:00", end: "24:00", weekdays: [1, 2, 3, 4, 5, 6, 7] },
};

const num = (v) => (v === null || v === undefined || v === "" || isNaN(Number(v)) ? null : Number(v));

export const isSubscription = (t) => !!t && (SUBSCRIPTIONS.includes(t.type) || t.recurring === true);
export const isNightType = (t) => !!t && (t.type === "night" || t.type === "nightsub");

export const normalizeTariff = (raw) => {
  if (!raw || !TARIFF_ORDER.includes(raw.type)) return null;
  const d = DEFAULTS[raw.type];
  return {
    id: String(raw.id ?? raw.tariffId ?? raw.type),
    type: raw.type,
    name: raw.name || null,
    price: num(raw.price) || 0,
    maxPerDay: num(raw.maxPerDay),
    start: raw.start || d.start || null,
    end: raw.end || d.end || null,
    weekdays: Array.isArray(raw.weekdays) ? raw.weekdays.map(Number) : d.weekdays || null,
    free: num(raw.free ?? raw.available),
    recurring: SUBSCRIPTIONS.includes(raw.type) || raw.recurring === true,
  };
};

// Alte API: ein Preis je Suche (stündlich oder monatlich)
const legacyTariffs = (raw, ctx = {}) => {
  const price = num(raw.price);
  if (!price) return [];
  if (ctx.activeTab === "parking-monthly") {
    return [normalizeTariff({ id: "legacy-month", type: ctx.duration === "Mon-Fri" ? "workday" : "full", price })];
  }
  if (ctx.activeTab === "parking-night") return [normalizeTariff({ id: "legacy-night", type: "night", price })];
  return [normalizeTariff({ id: "legacy-hour", type: "hour", price })];
};

export const normalizeLocation = (raw, ctx) => {
  if (!raw) return null;
  const id = raw.parkingId ?? raw.id ?? raw._id;
  if (id === undefined || id === null) return null;
  const tariffs = (Array.isArray(raw.tariffs) ? raw.tariffs.map(normalizeTariff) : legacyTariffs(raw, ctx))
    .filter(Boolean)
    .sort((a, b) => TARIFF_ORDER.indexOf(a.type) - TARIFF_ORDER.indexOf(b.type));
  const images = Array.isArray(raw.images) ? raw.images.filter(Boolean) : raw.imageUrl ? [raw.imageUrl] : [];
  return {
    id: String(id),
    code: raw.code || raw.parkingCode || null,
    title: raw.title || raw.name || "",
    address: raw.address || "",
    lat: num(raw.lat),
    lng: num(raw.lng),
    images,
    openingHours: raw.openingHours || null,
    spaces: num(raw.spaces ?? raw.totalSpaces),
    free: num(raw.freeSpaces ?? raw.available),
    maxHeight: num(raw.maxHeight),
    note: raw.note || null,
    tariffs,
  };
};

// ---------- Zeit & Preis ----------

const pad = (n) => String(n).padStart(2, "0");
export const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const toTimeInput = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const combine = (date, time) => (date ? new Date(`${date}T${time || "00:00"}`) : null);

const atTime = (date, hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  const d = new Date(date);
  d.setHours(h === 24 ? 0 : h, m || 0, 0, 0);
  if (h === 24) d.setDate(d.getDate() + 1);
  return d;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Start/Ende eines Zeitfenster-Tarifs ab einem Datum (Nacht 18–7 endet am Folgetag)
export const windowFor = (tariff, date, count = 1) => {
  const start = atTime(date, tariff.start);
  let end = atTime(date, tariff.end);
  if (end <= start) end = addDays(end, 1);
  return { start, end: addDays(end, Math.max(1, count) - 1) };
};

// Angefangene Stunden mit Tageshöchstpreis je 24 h
const hourPrice = (tariff, hours) => {
  if (!tariff.maxPerDay) return hours * tariff.price;
  const full = Math.floor(hours / 24);
  const rest = hours % 24;
  return full * Math.min(24 * tariff.price, tariff.maxPerDay) + Math.min(rest * tariff.price, tariff.maxPerDay);
};

/**
 * Preis und Zeitraum für eine Auswahl.
 * sel: { from, until } (Date) für "hour"; { date, count } für "day"/"night"; { date, weekdays } für Abos
 * Rückgabe: { ok, start, end, hours, count, total, monthly }
 */
export const quote = (tariff, sel) => {
  if (!tariff) return { ok: false };
  if (tariff.type === "hour") {
    const { from, until } = sel;
    if (!from || !until || isNaN(from) || isNaN(until) || until <= from) return { ok: false };
    const hours = Math.ceil((until - from) / 36e5);
    return { ok: true, start: from, end: until, hours, total: hourPrice(tariff, hours), monthly: false };
  }
  if (!sel.date) return { ok: false };
  const day = combine(sel.date, "00:00");
  if (isSubscription(tariff)) {
    return { ok: true, start: atTime(day, tariff.start === "24:00" ? "00:00" : tariff.start), end: null, total: tariff.price, monthly: true };
  }
  const count = Math.max(1, Number(sel.count) || 1);
  if (tariff.type === "day") {
    return { ok: true, start: day, end: addDays(day, count), count, total: count * tariff.price, monthly: false };
  }
  const w = windowFor(tariff, day, count);
  return { ok: true, start: w.start, end: w.end, count, total: count * tariff.price, monthly: false };
};

// Welcher Tarif passt "jetzt" (Einstieg per QR-Code)?
export const suggestNow = (location, now = new Date()) => {
  if (!location) return null;
  const by = (type) => location.tariffs.find((t) => t.type === type);
  const night = by("night");
  if (night) {
    const today = windowFor(night, now);
    const yesterday = windowFor(night, addDays(now, -1));
    // Laufende Nacht (nach Mitternacht) oder ab 15 Uhr die kommende Nacht
    if (now >= yesterday.start && now < yesterday.end) return { tariff: night, date: toDateInput(addDays(now, -1)), window: yesterday };
    if (now.getTime() >= today.start.getTime() - 3 * 36e5 && now < today.end) return { tariff: night, date: toDateInput(now), window: today };
  }
  const hour = by("hour");
  if (hour) return { tariff: hour, from: now, until: new Date(now.getTime() + 2 * 36e5) };
  const day = by("day");
  if (day) return { tariff: day, date: toDateInput(now) };
  return null;
};

// ---------- Anzeige ----------

const LOCALES = { de: "de-DE", en: "en-GB", es: "es-ES", it: "it-IT", tr: "tr-TR", pl: "pl-PL", fr: "fr-FR", nl: "nl-NL" };
export const locale = (lang) => LOCALES[(lang || "de").slice(0, 2)] || "de-DE";
export const euro = (n, lang = "de") => Number(n || 0).toLocaleString(locale(lang), { style: "currency", currency: "EUR" });

export const fmtDate = (d, lang = "de", opts = {}) =>
  d ? new Date(d).toLocaleDateString(locale(lang), { weekday: "short", day: "2-digit", month: "2-digit", ...opts }) : "";
export const fmtTime = (d, lang = "de") => (d ? new Date(d).toLocaleTimeString(locale(lang), { hour: "2-digit", minute: "2-digit" }) : "");
export const fmtDateTime = (d, lang = "de") => (d ? `${fmtDate(d, lang)} ${fmtTime(d, lang)}` : "");

export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export const weekdayShort = (n, lang = "de") => {
  // 2024-01-01 war ein Montag
  const d = new Date(2024, 0, n);
  return d.toLocaleDateString(locale(lang), { weekday: "short" }).replace(".", "");
};
